import { useEditorLocalState } from "@circle-react-shared/uikit/TipTap/utilities/useEditorLocalState";
import { TipTapEditorContext } from "@circle-react-uikit/TipTap/TipTapEditorContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { Editor, JSONContent } from "@tiptap/core";
import { EditorContent } from "@tiptap/react";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import type { ReactNode } from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { t } from "@/i18n-js/instance";

import { FileAttachmentButton } from "../Attachments/FileAttachmentButton";
import { ActionButton } from "./ActionButton";
import { ResourceLinkButton } from "./ResourceLinkButton";
import {
  tiptapToAnnotatedText,
  hasMeaningfulContent,
} from "./tiptapToAnnotatedText";
import type { TipTapComposerHandle } from "./types";
import { useCircleAiEditor } from "./useCircleAiEditor";
import { VoiceInputButton } from "./VoiceInputButton";

import scrollbarStyles from "./scrollbar.module.css";

interface TipTapComposerProps {
  disabled?: boolean;
  // Resting height (px) of the editor input area. Defaults to the shared 20px
  // floor; raise it to make the composer taller on a given surface.
  editorMinHeight?: number;
  hasSendableAttachments?: boolean;
  initialDraft?: string;
  isRunning?: boolean;
  isStopping?: boolean;
  isUploading?: boolean;
  onAddFiles?: (files: File[]) => void;
  onDraftConsumed?: () => void;
  onSend: (annotatedText: string, tiptapBody: JSONContent) => void;
  onStop?: () => void;
  onVoiceActiveChange?: (isActive: boolean) => void;
  placeholder?: string;
  testId: string;
  toolbarAccessory?: ReactNode;
}

export const TipTapComposer = forwardRef<
  TipTapComposerHandle,
  TipTapComposerProps
>(
  (
    {
      onSend,
      onStop,
      disabled,
      editorMinHeight,
      hasSendableAttachments = false,
      isRunning = false,
      isStopping = false,
      isUploading = false,
      onAddFiles,
      placeholder = t("circle_ai_home.composer_placeholder"),
      initialDraft,
      onDraftConsumed,
      onVoiceActiveChange,
      testId,
      toolbarAccessory,
    }: TipTapComposerProps,
    ref,
  ) => {
    const editorRef = useRef<Editor | null>(null);
    const [isVoiceActive, setIsVoiceActive] = useState(false);
    const {
      localSgidToObjectMap,
      addToLocalSgidToObjectMap,
      setLocalSgidToObjectMap,
    } = useEditorLocalState({});
    const sgidMapRef = useRef(localSgidToObjectMap);
    sgidMapRef.current = localSgidToObjectMap;

    const disabledRef = useRef(disabled);
    disabledRef.current = disabled || isRunning || isUploading;

    const hasSendableAttachmentsRef = useRef(hasSendableAttachments);
    hasSendableAttachmentsRef.current = hasSendableAttachments;

    const handleSubmit = useCallback(() => {
      if (disabledRef.current) return;
      const editor = editorRef.current;
      if (!editor || editor.isDestroyed) return;

      const json = editor.getJSON();
      // Attachment-only messages are allowed: only block when there is neither
      // meaningful text nor an attachment ready to send.
      if (!hasMeaningfulContent(json) && !hasSendableAttachmentsRef.current)
        return;

      const sgidMap = sgidMapRef.current;
      const annotatedText = tiptapToAnnotatedText(json, sgidMap);
      const tiptapBody = { ...json, sgids_to_object_map: sgidMap };
      onSend(annotatedText, tiptapBody);
      editor.commands.clearContent(true);
      setLocalSgidToObjectMap({});
    }, [onSend, setLocalSgidToObjectMap]);

    const handleTranscript = useCallback((text: string) => {
      const transcript = text.trim();
      if (!transcript) return;

      const editor = editorRef.current;
      if (!editor || editor.isDestroyed) return;

      if (hasMeaningfulContent(editor.getJSON())) {
        editor.commands.insertContent(` ${transcript}`);
      } else {
        editor.commands.setContent(transcript, { emitUpdate: true });
      }
      editor.commands.focus("end");
    }, []);

    const { editor, hasContent } = useCircleAiEditor({
      placeholder,
      onSubmit: handleSubmit,
    });

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          if (!editor || editor.isDestroyed) return;
          editor.commands.focus();
        },
      }),
      [editor],
    );

    const isComposerInteractionDisabled = Boolean(disabled || isVoiceActive);

    useEffect(() => {
      editorRef.current = editor;
      if (!editor || editor.isDestroyed) return;
      editor.setEditable(!disabled);
      if (!disabled) editor.commands.focus();
    }, [editor, disabled]);

    const onDraftConsumedRef = useRef(onDraftConsumed);
    onDraftConsumedRef.current = onDraftConsumed;

    useEffect(() => {
      if (!editor || editor.isDestroyed || !initialDraft) return;
      editor.commands.setContent(initialDraft, { emitUpdate: true });
      editor.commands.focus("end");
      onDraftConsumedRef.current?.();
    }, [editor, initialDraft]);

    const handleVoiceActiveChange = useCallback(
      (isActive: boolean) => {
        setIsVoiceActive(isActive);
        onVoiceActiveChange?.(isActive);
      },
      [onVoiceActiveChange],
    );

    const handlePaste = useCallback(
      (e: React.ClipboardEvent) => {
        if (disabled || !onAddFiles) return;

        const files = Array.from(e.clipboardData?.files ?? []);
        if (files.length === 0) return;

        e.preventDefault();
        onAddFiles(files);
      },
      [disabled, onAddFiles],
    );

    const contextValue = useMemo(
      () =>
        ({
          addToLocalSgidToObjectMap,
          localSgidToObjectMap,
          sgidToObjectMap: {},
          chatProps: { isThread: true },
          type: "chat",
          isUpdate: false,
        }) as any,
      [addToLocalSgidToObjectMap, localSgidToObjectMap],
    );

    // A surface can raise the editor's resting height. Applied only when not
    // recording, so the voice-active collapse (min-h-0/max-h-0) still wins.
    const hasCustomEditorHeight = editorMinHeight !== undefined;
    const shouldApplyCustomEditorHeight =
      hasCustomEditorHeight && !isVoiceActive;

    return (
      <TipTapEditorContext.Provider value={contextValue}>
        <OverlayScrollbarsComponent
          className={classNames(
            "overflow-hidden transition-[max-height,min-height,opacity] duration-200 ease-out motion-reduce:transition-none",
            {
              "pointer-events-none max-h-0 min-h-0 opacity-0": isVoiceActive,
              "max-h-[120px] opacity-100": !isVoiceActive,
              "min-h-[20px]": !isVoiceActive && !hasCustomEditorHeight,
            },
            {
              "cursor-not-allowed": disabled,
              "cursor-text": !disabled,
            },
          )}
          style={
            shouldApplyCustomEditorHeight
              ? { minHeight: editorMinHeight }
              : undefined
          }
          data-testid={`${testId}-editor`}
          onPaste={isComposerInteractionDisabled ? undefined : handlePaste}
          options={{
            scrollbars: {
              autoHide: "never",
              theme: scrollbarStyles.composerScroll,
            },
          }}
        >
          <EditorContent editor={editor} />
        </OverlayScrollbarsComponent>
        <div className="flex flex-row items-center justify-between">
          <div
            className={classNames(
              "flex max-w-24 origin-left items-center gap-1 overflow-hidden transition-[max-width,opacity,transform] duration-200 ease-out motion-reduce:transition-none",
              {
                "pointer-events-none max-w-0 -translate-x-1 scale-95 opacity-0":
                  isVoiceActive,
                "translate-x-0 scale-100 opacity-100": !isVoiceActive,
              },
            )}
          >
            <ResourceLinkButton
              disabled={isComposerInteractionDisabled}
              editor={editor}
            />
            {onAddFiles && (
              <FileAttachmentButton
                disabled={isComposerInteractionDisabled}
                onAddFiles={onAddFiles}
              />
            )}
          </div>
          <div
            className={classNames(
              "flex min-w-0 items-center transition-[gap] duration-200 ease-out motion-reduce:transition-none",
              {
                "gap-0": isVoiceActive,
                "gap-2": !isVoiceActive,
                "flex-1": isVoiceActive,
              },
            )}
          >
            {toolbarAccessory}
            <VoiceInputButton
              disabled={disabled}
              onActiveChange={handleVoiceActiveChange}
              onTranscript={handleTranscript}
            />
            <div
              className={classNames(
                "origin-right overflow-hidden transition-[opacity,transform,width] duration-200 ease-out motion-reduce:transition-none",
                {
                  "pointer-events-none w-0 scale-95 opacity-0": isVoiceActive,
                  "w-8 scale-100 opacity-100": !isVoiceActive,
                },
              )}
            >
              <ActionButton
                disabled={disabled}
                canSend={hasContent || hasSendableAttachments}
                isRunning={isRunning}
                isStopping={isStopping}
                isUploading={isUploading}
                isVoiceActive={isVoiceActive}
                onStop={onStop}
                onSubmit={handleSubmit}
                testId={testId}
              />
            </div>
          </div>
        </div>
      </TipTapEditorContext.Provider>
    );
  },
);

TipTapComposer.displayName = "TipTapComposer";
